import app, { db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

export const TEKNOPARK_AI_MODULE_ID = "teknopark-ai";

export const DEFAULT_TEKNOPARK_AI_MODULE = {
  id: TEKNOPARK_AI_MODULE_ID,
  name: "Teknopark AI",
  description: "Teknopark mevzuatı ve süreçleri için doğrulanmış bilgi asistanı",
  creditPrice: 1000,
  durationDays: 30,
  questionLimit: 250,
  isActive: true
};

const callAI = async (name, data = {}) => {
  const callable = httpsCallable(getFunctions(app, "us-central1"), name);
  return (await callable(data)).data;
};

const snapshotData = (snapshot) => (
  snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null
);

export const ensureTeknoparkAIModule = () =>
  setDoc(
    doc(db, "moduleCatalog", TEKNOPARK_AI_MODULE_ID),
    {
      ...DEFAULT_TEKNOPARK_AI_MODULE,
      updatedAt: serverTimestamp()
    },
    { merge: true }
  );

export const subscribeTeknoparkAIModule = (callback, onError) =>
  onSnapshot(
    doc(db, "moduleCatalog", TEKNOPARK_AI_MODULE_ID),
    (snapshot) => callback(snapshotData(snapshot) || DEFAULT_TEKNOPARK_AI_MODULE),
    onError
  );

export const subscribeTenantAIState = (tenantId, callback, onError) => {
  if (!tenantId) {
    callback({
      module: DEFAULT_TEKNOPARK_AI_MODULE,
      entitlement: null,
      wallet: null
    });
    return () => {};
  }

  let currentModule = DEFAULT_TEKNOPARK_AI_MODULE;
  let currentEntitlement = null;
  let currentWallet = null;
  const emit = () => callback({
    module: currentModule,
    entitlement: currentEntitlement,
    wallet: currentWallet
  });

  const unsubscribers = [
    onSnapshot(
      doc(db, "moduleCatalog", TEKNOPARK_AI_MODULE_ID),
      (snapshot) => {
        currentModule = snapshotData(snapshot) || DEFAULT_TEKNOPARK_AI_MODULE;
        emit();
      },
      onError
    ),
    onSnapshot(
      doc(db, "tenants", tenantId, "moduleEntitlements", TEKNOPARK_AI_MODULE_ID),
      (snapshot) => {
        currentEntitlement = snapshotData(snapshot);
        emit();
      },
      onError
    ),
    onSnapshot(
      doc(db, "tenants", tenantId, "billing", "wallet"),
      (snapshot) => {
        currentWallet = snapshotData(snapshot) || {
          id: "wallet",
          balance: 0,
          currency: "ARGEVIO_CREDIT"
        };
        emit();
      },
      onError
    )
  ];

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
};

export const subscribeAIKnowledgeItems = (callback, onError) =>
  onSnapshot(
    collection(db, "aiKnowledgeItems"),
    (snapshot) => {
      const items = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      items.sort((left, right) => {
        const leftTime = left.updatedAt?.toMillis?.() || left.createdAt?.toMillis?.() || 0;
        const rightTime = right.updatedAt?.toMillis?.() || right.createdAt?.toMillis?.() || 0;
        return rightTime - leftTime;
      });
      callback(items);
    },
    onError
  );

export const saveAIKnowledgeItem = async (item, actor = {}) => {
  const payload = {
    question: String(item.question || "").trim(),
    alternativeQuestions: Array.isArray(item.alternativeQuestions)
      ? item.alternativeQuestions.map((value) => String(value).trim()).filter(Boolean)
      : [],
    answer: String(item.answer || "").trim(),
    category: String(item.category || "Genel").trim(),
    tags: Array.isArray(item.tags)
      ? item.tags.map((value) => String(value).trim()).filter(Boolean)
      : [],
    sourceTitle: String(item.sourceTitle || "").trim(),
    sourceUrl: String(item.sourceUrl || "").trim(),
    validFrom: String(item.validFrom || "").trim(),
    validUntil: String(item.validUntil || "").trim(),
    audience: item.audience === "tenant" ? "tenant" : "global",
    tenantIds: item.audience === "tenant"
      ? (item.tenantIds || []).map((value) => String(value).trim()).filter(Boolean)
      : [],
    status: item.status === "published" ? "published" : "draft",
    updatedBy: actor.uid || "",
    updatedByEmail: actor.email || "",
    updatedAt: serverTimestamp()
  };

  if (!payload.question || !payload.answer) {
    throw new Error("Soru ve doğrulanmış cevap zorunludur.");
  }
  if (payload.audience === "tenant" && payload.tenantIds.length === 0) {
    throw new Error("Müşteriye özel kayıtta en az bir müşteri seçilmelidir.");
  }

  if (item.id) {
    await updateDoc(doc(db, "aiKnowledgeItems", item.id), payload);
    return item.id;
  }

  const created = await addDoc(collection(db, "aiKnowledgeItems"), {
    ...payload,
    createdBy: actor.uid || "",
    createdByEmail: actor.email || "",
    createdAt: serverTimestamp()
  });
  return created.id;
};

export const deleteAIKnowledgeItem = (id) =>
  deleteDoc(doc(db, "aiKnowledgeItems", id));

export const subscribeAIProcessDrafts = (callback, onError) =>
  onSnapshot(
    collection(db, "aiProcessDrafts"),
    (snapshot) => {
      const drafts = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      drafts.sort((left, right) =>
        (right.updatedAt?.toMillis?.() || right.createdAt?.toMillis?.() || 0) -
        (left.updatedAt?.toMillis?.() || left.createdAt?.toMillis?.() || 0)
      );
      callback(drafts);
    },
    onError
  );

export const saveAIProcessDraft = async (draft, actor = {}) => {
  const payload = {
    title: String(draft.title || "").trim(),
    category: String(draft.category || "Genel").trim(),
    sourceTitle: String(draft.sourceTitle || "").trim(),
    overview: String(draft.overview || "").trim(),
    applicability: String(draft.applicability || "").trim(),
    legalBasis: String(draft.legalBasis || "").trim(),
    deadline: String(draft.deadline || "").trim(),
    exceptions: String(draft.exceptions || "").trim(),
    steps: Array.isArray(draft.steps)
      ? draft.steps.map((step, index) => ({
        order: index + 1,
        title: String(step.title || step || "").trim(),
        completed: false
      })).filter((step) => step.title)
      : [],
    status: "draft",
    updatedBy: actor.uid || "",
    updatedByEmail: actor.email || "",
    updatedAt: serverTimestamp()
  };

  if (!payload.title || payload.steps.length === 0) {
    throw new Error("Süreç başlığı ve en az bir süreç adımı zorunludur.");
  }

  if (draft.id) {
    await updateDoc(doc(db, "aiProcessDrafts", draft.id), payload);
    return draft.id;
  }

  const created = await addDoc(collection(db, "aiProcessDrafts"), {
    ...payload,
    createdBy: actor.uid || "",
    createdByEmail: actor.email || "",
    createdAt: serverTimestamp()
  });
  return created.id;
};

export const deleteAIProcessDraft = (id) =>
  deleteDoc(doc(db, "aiProcessDrafts", id));

export const subscribeAITenants = (callback, onError) =>
  onSnapshot(
    collection(db, "tenants"),
    (snapshot) => {
      const tenants = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((left, right) =>
          String(left.name || left.companyName || left.id)
            .localeCompare(String(right.name || right.companyName || right.id), "tr")
        );
      callback(tenants);
    },
    onError
  );

export const getTenantAIState = async (tenantId) => {
  const [walletSnapshot, entitlementSnapshot, ledgerSnapshot] = await Promise.all([
    getDoc(doc(db, "tenants", tenantId, "billing", "wallet")),
    getDoc(doc(db, "tenants", tenantId, "moduleEntitlements", TEKNOPARK_AI_MODULE_ID)),
    getDocs(collection(db, "tenants", tenantId, "creditLedger"))
  ]);
  const ledger = ledgerSnapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .sort((left, right) =>
      (right.createdAt?.toMillis?.() || 0) - (left.createdAt?.toMillis?.() || 0)
    );

  return {
    wallet: snapshotData(walletSnapshot) || { balance: 0, currency: "ARGEVIO_CREDIT" },
    entitlement: snapshotData(entitlementSnapshot),
    ledger: ledger.slice(0, 10)
  };
};

export const subscribeAIConversations = ({ tenantId, userId, onData, onError }) => {
  if (!tenantId || !userId) {
    onData([]);
    return () => {};
  }
  return onSnapshot(
    query(
      collection(db, "tenants", tenantId, "aiConversations"),
      where("userId", "==", userId)
    ),
    (snapshot) => {
      const conversations = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .sort((left, right) =>
          (right.updatedAt?.toMillis?.() || 0) - (left.updatedAt?.toMillis?.() || 0)
        );
      onData(conversations);
    },
    onError
  );
};

export const subscribeAIConversationMessages = ({
  tenantId,
  conversationId,
  onData,
  onError
}) => {
  if (!tenantId || !conversationId) {
    onData([]);
    return () => {};
  }
  return onSnapshot(
    query(
      collection(db, "tenants", tenantId, "aiConversations", conversationId, "messages"),
      orderBy("createdAt", "asc")
    ),
    (snapshot) => {
      const messages = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      messages.sort((left, right) => {
        const timeDifference =
          (left.createdAt?.toMillis?.() || 0) - (right.createdAt?.toMillis?.() || 0);
        if (timeDifference !== 0) return timeDifference;

        if (left.turnId && right.turnId) {
          const turnDifference = String(left.turnId).localeCompare(String(right.turnId));
          if (turnDifference !== 0) return turnDifference;
          return Number(left.turnPosition || 0) - Number(right.turnPosition || 0);
        }

        const roleOrder = { user: 0, assistant: 1 };
        return (roleOrder[left.role] ?? 2) - (roleOrder[right.role] ?? 2);
      });
      onData(messages);
    },
    onError
  );
};

export const askTeknoparkAI = ({ tenantId, question, conversationId = "" }) =>
  callAI("askTeknoparkAI", { tenantId, question, conversationId });

export const requestAIHumanSupport = ({ tenantId, conversationId }) =>
  callAI("requestAIHumanSupport", { tenantId, conversationId });

export const purchaseTeknoparkAIWithCredits = (tenantId) =>
  callAI("purchaseTeknoparkAIWithCredits", { tenantId });

export const adminGrantCredits = ({ tenantId, amount, note }) =>
  callAI("adminGrantCredits", { tenantId, amount, note });

export const adminSetTeknoparkAIEntitlement = ({
  tenantId,
  status = "active",
  durationDays = 30,
  questionLimit = 250
}) => callAI("adminSetTeknoparkAIEntitlement", {
  tenantId,
  status,
  durationDays,
  questionLimit
});
